import React, { useState, useEffect, useRef, useMemo } from 'react'
import MaterialTable, { MTableToolbar, MTableCell, MTableBodyRow, MTableGroupRow } from '@material-table/core'
import { useDispatch } from 'react-redux'
import { Link as RouterLink } from 'react-router-dom'

import { MuiThemeProvider, makeStyles } from '@material-ui/core/styles'
import { table } from '../../services/theme'
import moment from 'moment'
import { Typography, MenuItem, Link, Tooltip } from '@material-ui/core'
import TableDropdown from './TableDropdown'
import RetryChargeDialog from '../Dialogs/Payments/RetryChargeDialog'
import RefundDialog from '../Dialogs/Payments/RefundDialog'
import { useSelector } from 'react-redux'
import { showSuccess, showError } from '../../actions/misc/misc.actions'
import { getBalances, getTransactions } from '../../actions/households/households.actions'
import { api } from '../../services/api'
import { LINE_ITEM_TYPES, formatNumsToDollars } from '../../utils'
import { PaymentsToolbar } from './Toolbars/Household/PaymentsToolbar'
import { gray } from '../../utils/colors'
const useStyles = makeStyles((theme) => ({
	tableWrapper: {
		'& table': {
			backgroundColor: 'transparent !important',
			background: 'transparent !important'
		},
		'& th': {
			color: '#1A1A35',
			background: 'transparent',
			fontWeight: 800
		},
		'& thead': {
			'& tr': {
				backgroundColor: 'transparent !important',
				background: 'transparent !important'
			}
		}
	},
	appointmentTitle: {
		fontWeight: 600,
		fontSize: '16px',
		lineHeight: '22px'
	},
	appointmentDescription: {
		fontSize: '14px',
		lineHeight: '24px',
		color: '#777679'
	},
	chargeMeta: {
		fontSize: '12px',
		lineHeight: '16px',
		color: '#777679'
	},
	green: {
		color: '#2E7B32'
	},
	red: {
		color: '#D02C2C'
	},
	toolbar: {
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'wrap',
		padding: '24px 25px',
		'& > div:nth-child(1)': {
			'& > div:nth-child(1)': {
				overflow: 'visible'
			}
		}
	}
}))

const TransactionsTable = (props) => {
	const classes = useStyles()
	const facilities = useSelector((state) => state.specific.facilities.facilities)
	const filters = useSelector((state) => state.specific.households.selectedLineItemFilters)
	const [retryModalOpen, setRetryModalOpen] = useState(false)
	const [refundModal, setRefundModal] = useState(false)
	const [refundCharge, setRefundCharge] = useState(null)
	const [chargeAmount, setChargeAmount] = useState(0)
	const [retryCharges, setRetryCharges] = useState([])
	const [charges, setCharges] = useState([])
	const [chargeError, setChargeError] = useState(false)
	const [loading, setLoading] = useState(false)
	const { household } = props
	const dispatch = useDispatch()

	let { data } = props

	const sortedList = useMemo(() => {
		const { client_transactions, gift_cards, promotions } = filters
		const other_types = [
			'addon',
			'daycare',
			'boarding',
			'grooming',
			'grooming_tip',
			'petcare_cancellation',
			'petcare_noshow',
			'grooming_addon',
			'early_dropoff',
			'extended_pickup',
			'membership'
		]
		const descriptions = ['cancellation', 'add dog']
		return data.filter((item) => {
			if (!client_transactions.length && !gift_cards && !promotions) return true

			if (client_transactions?.length) {
				if (client_transactions.includes('bundle')) {
					if (
						item?.description?.toLowerCase().includes('bundle') ||
						item?.type === 'daycare_promo_credit'
					)
						return true
				}
				if (client_transactions.includes('expired_credits')) {
					if (item?.title?.toLowerCase().includes('expired')) return true
				}
				if (client_transactions.includes('credit_deposits')) {
					if (item?.type === 'credit') return true
				}
				if (client_transactions.includes('used_credits')) {
					if (item?.type === 'credits_used') return true
				}
				if (client_transactions.includes('other')) {
					if (
						other_types.includes(item?.type) ||
						descriptions.some((d) => item?.description?.toLowerCase().includes(d))
					)
						return true
				}
			}

			if (gift_cards) {
				if (item.type === 'gift_card') return true
			}

			if (promotions) {
				if (
					(item?.type?.toLowerCase().includes('promo') ||
						item?.subtype?.toLowerCase().includes('promo')) &&
					item?.type !== 'daycare_promo_credit'
				)
					return true
			}

			return false
		})
	}, [data, filters])

	const refundGrouped = useMemo(() => {
		if (!refundCharge) return null

		const related = data.filter((d) => d.stripe_charge_id === refundCharge.stripe_charge_id)

		return related
	}, [refundCharge])

	useEffect(() => {
		const retrieveStripeCharge = async () => {
			if (refundCharge) {
				try {
					const { data } = await api.get(`/clients/stripeCharge/${refundCharge.stripe_charge_id}`)
					setChargeAmount(data.amount)
				} catch (error) {
					dispatch(showError('Error retrieving charge'))
				}
			}
		}

		retrieveStripeCharge()
	}, [refundCharge])

	const renderChargeMeta = (data) => {
		const facilityId = data.facility_id || data.visit?.facility_id
		const facility = (facilities || []).find((entry) => String(entry._id) === String(facilityId))
		const facilityName = data.facility_name || facility?.name
		const stripeKey = data.stripe_key

		if (!facilityName && !stripeKey) return null

		const lines = []
		if (facilityName) lines.push(facilityName)
		if (stripeKey) lines.push(`Stripe: ${stripeKey}`)

		return (
			<Tooltip title={lines.join(' · ')}>
				<div>
					{lines.map((line) => (
						<Typography key={line} className={classes.chargeMeta}>
							{line}
						</Typography>
					))}
				</div>
			</Tooltip>
		)
	}

	const renderDescription = (data) => {
		let credits

		if (data.daycareCredits && data.daycareCredits.length > 0) {
			credits = data.daycareCredits
		}

		if (data.playcareCredits && data.playcareCredits.length > 0) {
			credits = data.playcareCredits
		}

		if (data.walkCredits && data.walkCredits.length > 0) {
			credits = data.walkCredits
		}

		let isMembershipCharge =
			data.type === 'autorenew' ||
			data.type === 'membership' ||
			data.description?.toLowerCase().includes('member')

		if (isMembershipCharge)
			return (
				<div style={{ display: 'flex', flexDirection: 'column' }}>
					<Typography className={classes.appointmentTitle}>Skiptown Membership Charge</Typography>
					<Typography className={classes.appointmentDescription}>
						{data.subtext || data.description}
					</Typography>
					{renderChargeMeta(data)}
				</div>
			)

		if (data.type === 'visit_promo' && data.visit && data.visit.household_id && data.visit._id) {
			return (
				<div style={{ display: 'flex', flexDirection: 'column' }}>
					<Link
						component={RouterLink}
						to={`/clients/${data.visit.household_id}/appointments/${data.visit._id}`}
						style={{
							color: '#6B4AD6',
							fontSize: '16px',
							fontWeight: 600
						}}
						className={classes.appointmentTitle}>{`${getVisitDate(data.visit)} - ${
						data.description
					}`}</Link>

					{data.subtext && (
						<Typography className={classes.appointmentDescription}>{data.subtext}</Typography>
					)}
					{renderChargeMeta(data)}
				</div>
			)
		}

		if (data.type === 'credits_used') {
			return (
				<div style={{ display: 'flex', flexDirection: 'column' }}>
					<Link
						component={RouterLink}
						to={`/clients/${data.visit.household_id}/appointments/${data.visit._id}`}
						style={{
							color: '#6B4AD6',
							fontSize: '16px',
							fontWeight: 600
						}}
						className={classes.appointmentTitle}>
						{data.description}
					</Link>
					{renderChargeMeta(data)}
				</div>
			)
		}
		if (data.type === LINE_ITEM_TYPES.Training) {
			return (
				<div style={{ display: 'flex', flexDirection: 'column' }}>
					<Link
						component={RouterLink}
						to={`/facilities/${data.facility_id}/training/class/${data.training_class_id}`}
						style={{
							color: '#6B4AD6',
							fontSize: '16px',
							fontWeight: 600
						}}
						className={classes.appointmentTitle}>
						{data.description}
					</Link>
					{renderChargeMeta(data)}
				</div>
			)
		}

		if (data?.visit?.type === 'addon_only') {
			return (
				<div style={{ display: 'flex', flexDirection: 'column' }}>
					{data.appointment ? (
						<Typography className={classes.appointmentTitle}>{`${getAppointmentDate(
							data.appointment
						)}`}</Typography>
					) : null}

					{data.visit && data.visit.facility_id && data.visit.household_id && data.visit._id ? (
						<Link
							component={RouterLink}
							to={`/clients/${data.visit.household_id}/appointments/${data.visit._id}`}
							style={{
								color: '#6B4AD6',
								fontSize: '16px',
								fontWeight: 600
							}}
							className={classes.appointmentTitle}>{`${getVisitDate(data.visit)}${
							data.visit.type ? ` - Enrichment Only` : null
						}`}</Link>
					) : null}
					{renderChargeMeta(data)}
				</div>
			)
		}

		return (
			<div style={{ display: 'flex', flexDirection: 'column' }}>
				{credits && credits.length > 0 ? (
					<Typography className={classes.appointmentTitle}>{`${getCreditsTitle(
						credits[0]
					)}`}</Typography>
				) : null}
				{data.appointment ? (
					<Typography className={classes.appointmentTitle}>{`${getAppointmentDate(
						data.appointment
					)}`}</Typography>
				) : null}

				{data.visit && data.visit.facility_id && data.visit.household_id && data.visit._id ? (
					<Link
						component={RouterLink}
						to={`/clients/${data.visit.household_id}/appointments/${data.visit._id}`}
						style={{
							color: '#6B4AD6',
							fontSize: '16px',
							fontWeight: 600
						}}
						className={classes.appointmentTitle}>{`${getVisitDate(data.visit)}${
						data.visit.type
							? ` - ${data.visit.type.substring(0, 1).toUpperCase()}${data.visit.type.substring(1)}`
							: null
					}`}</Link>
				) : null}
				{(!credits && data.description) ||
				(credits && credits.length > 0 && !credits[0].discount && data.description) ? (
					<Typography className={classes.appointmentDescription}>{data.description}</Typography>
				) : null}
				{data.type === 'petcare_cancellation' && (
					<Typography className={classes.appointmentDescription}>Cancellation Fee</Typography>
				)}
				{data.type === 'petcare_noshow' && (
					<Typography className={classes.appointmentDescription}>No-Show Fee</Typography>
				)}
				{credits && credits.length > 0 && credits[0].discount ? (
					<Typography className={classes.appointmentDescription}>{`Discounted ${
						credits[0].discount * 100
					}% ${credits[0].promo_type}`}</Typography>
				) : null}
				{data.subtext && (
					<Typography className={classes.appointmentDescription}>{data.subtext}</Typography>
				)}
				{renderChargeMeta(data)}
			</div>
		)
	}

	const getCreditsTitle = (credit) => {
		let { type, bundle_size } = credit
		let isDaycare = type === 'daycare'
		let isPlaycare = type === 11

		return `${bundle_size} - ${isDaycare ? 'daycare' : isPlaycare ? 'playcare' : 'walk'} ${
			!isDaycare && !isPlaycare ? `bundle (${type} min visits)` : 'bundle'
		}`
	}

	const getAppointmentDate = (appointment) => {
		let date = appointment.visit_date ? moment(appointment.visit_date) : null
		let appointmentDetail = null

		if (date) {
			let day = date.format('D')
			let month = date.format('MMM')

			appointmentDetail = `${month} ${day}${
				appointment.appointment_type ? ` - ${appointment.appointment_type.name}` : null
			}`
		}

		return appointmentDetail
	}

	const getVisitDate = (visit) => {
		let start = visit.startDate ? moment(visit.startDate) : null
		let end = visit.endDate ? moment(visit.endDate) : moment(visit.startDate)

		if (start && end) {
			let startDay = start.format('D')
			let endDay = end.format('D')
			let startMonth = start.format('MMM')
			let endMonth = end.format('MMM')

			let endDateDisplay = ''

			if (visit.type === 'boarding') {
				endDateDisplay =
					startMonth === endMonth && endDay
						? ` - ${endDay}`
						: startMonth !== endMonth && endDay
						? ` - ${endMonth} ${endDay}`
						: null
			}

			return `${startMonth} ${startDay}${endDateDisplay}`
		}
	}

	const renderAmount = (data) => {
		let amount = data.amount / 100

		if (data.type === LINE_ITEM_TYPES.VisitPromo) {
			if (data.subtype && data.subtype === LINE_ITEM_TYPES.AppliedPromoCode) {
				return <Typography className={classes.green}>{`+ ${formatNumsToDollars(data.amount)}`}</Typography>
			}
			return (
				<Typography className={classes.green}>
					{data.stripe_charge_status === 'succeeded'
						? `+ $${amount.toFixed(2).toString().substring(1)}`
						: `+ up to $${amount.toFixed(2).toString().substring(1)}`}
				</Typography>
			)
		}

		if (data.type === LINE_ITEM_TYPES.CreditsUsed) {
			return (
				<Typography className={classes.red}>
					- {data.credits_used} Credit{data.credits_used > 1 ? 's' : ''}
				</Typography>
			)
		}

		return data.type === LINE_ITEM_TYPES.Credit || amount.toString().indexOf('-') !== -1 ? (
			<Typography className={classes.green}>
				{amount !== null ? `+ $${amount.toFixed(2).toString().substring(1)}` : 'No Amount'}
			</Typography>
		) : (
			<Typography className={classes.red}>- ${amount.toFixed(2)}</Typography>
		)
	}

	const findRelatedCharges = (charge) => {
		if (!charge.visit_id) {
			setRetryCharges([charge])
		} else {
			const ourCharges = data.filter(
				(d) => d.visit_id === charge.visit_id && d?.stripe_charge_status?.toLowerCase() === 'failed'
			)
			setRetryCharges(ourCharges)
		}
	}

	const retryCharge = async (charges) => {
		try {
			setLoading(true)
			setChargeError(null)
			await api.post(`/clients/processCharges`, { charges })
			setRetryModalOpen(false)
			dispatch(showSuccess('Charge Successful'))
			dispatch(getTransactions(charges[0].household_id))
			setLoading(false)
			setRetryCharges([])
		} catch (err) {
			setChargeError(err?.data?.error || JSON.stringify(err?.data || err))
			dispatch(showError('Error processing charge'))
			dispatch(getTransactions(charges[0].household_id))
			setLoading(false)
		}
	}

	const processRefunds = async (obj) => {
		try {
			setLoading(true)
			await api.post(`/clients/refundCharges`, { charge: obj })
			setRefundModal(false)
			setRefundCharge(null)
			dispatch(showSuccess('Refund Successful'))
			dispatch(getTransactions(data[0].household_id))
			setLoading(false)
		} catch (err) {
			setChargeError(err?.data?.error || JSON.stringify(err?.data || err))
			dispatch(showError('Error refunding charge'))
			setLoading(false)
		}
	}

	const tableRef = useRef(null)
	const toolbarRef = useRef(null)

	return (
		<MuiThemeProvider theme={table}>
			<PaymentsToolbar ref={toolbarRef} tableRef={tableRef} />
			<div className={classes.tableWrapper}>
				<MaterialTable
					options={{
						searchFieldAlignment: 'left',
						search: false,
						sorting: false,
						grouping: false,
						pageSize: 20,
						pageSizeOptions: [20, 50],
						actionsColumnIndex: -1,
						headerStyle: {
							backgroundColor: 'transparent',
							background: 'transparent'
						},
						rowStyle: (rowData) => ({
							backgroundColor: rowData.isGrouped ? gray[300] : undefined,
							verticalAlign: 'top'
						}),
						actionsCellStyle: {
							paddingTop: 8
						}
					}}
					localization={{
						header: {
							actions: ''
						}
					}}
					style={{
						backgroundColor: 'transparent',
						background: 'transparent',
						boxShadow: 'none'
					}}
					actions={[{}]}
					columns={[
						{
							title: 'Date',
							field: 'createdAt',
							cellStyle: {
								fontWeight: 600
							},
							render: (data) => {
								if (data.isGrouped && !data.isFirst) return null
								return moment(data.createdAt).format('MMM DD, YYYY')
							}
						},
						{
							title: 'Transaction',
							field: 'description',
							render: (data) =>
								data.title ? (
									<div style={{ display: 'flex', flexDirection: 'column' }}>
										<Typography
											className={classes.appointmentTitle}
											style={{ color: data.type === 'member_dues' ? '#6B4AD6' : '#DC2A2A' }}>
											{data.title}
										</Typography>
										{data.description ? (
											<Typography className={classes.appointmentDescription}>
												{data.description}
											</Typography>
										) : null}
										{renderChargeMeta(data)}
									</div>
								) : (
									renderDescription(data)
								)
						},
						{
							title: 'Status',
							field: 'stripe_charge_status',
							cellStyle: {
								textTransform: 'capitalize'
							},
							render: (data) => {
								let text = data.stripe_charge_status
									? data.stripe_charge_status
									: data.title
									? ''
									: 'pending'

								if (data.refunded) text = 'Refunded'

								return (
									<Typography
										style={{
											color:
												(data.stripe_charge_status &&
													data.stripe_charge_status === 'succeeded') ||
												data.type === 'daycare_promo_credit'
													? '#A2A1A4'
													: '#B26200',
											fontSize: '16px'
										}}>
										{text}
									</Typography>
								)
							}
						},
						{
							title: 'Amount',
							field: 'amount',
							render: (data) => (data.title && data.type !== 'member_dues' ? '' : renderAmount(data))
						}
					]}
					data={sortedList}
					// Title
					title=""
					components={{
						Toolbar: (props) => null,
						Row: (props) => {
							const style = {}

							if (props.data.isGrouped) style.backgroundColor = gray[300]

							return <MTableBodyRow {...props} style={style} />
						},
						Action: (props) => {
							if (props.data.isGrouped && !props.data.isMain) return null

							if (props.data.stripe_charge_id === 'n/a') return null

							return (
								<TableDropdown>
									{props.data.stripe_charge_id ? (
										<div>
											<MenuItem
												onClick={() =>
													window.open(
														`https://dashboard.stripe.com${
															process.env.REACT_APP_ENV === 'production'
																? '/'
																: '/test/'
														}payments/${props.data.stripe_charge_id}`,
														'_blank'
													)
												}>
												View on Stripe
											</MenuItem>
											{/* 
											props.data.refunded ? null : 
											<MenuItem
												onClick={() => {
													setRefundModal(true)
													setRefundCharge(props.data)
												}}>
												Refund
											</MenuItem> 
											*/}
										</div>
									) : props.data.stripe_charge_status === 'failed' ? (
										<MenuItem
											onClick={() => {
												setRetryModalOpen(true)
												findRelatedCharges(props.data)
											}}>
											Retry Charge
										</MenuItem>
									) : (
										<MenuItem>No actions</MenuItem>
									)}
								</TableDropdown>
							)
						},
						Cell: (props) => {
							const style = {
								fontSize: '16px',
								lineHeight: '22px',
								padding: '18px 36px 13px 24px'
							}

							if (props?.rowData?.isGrouped && !props?.rowData?.isLast) style.borderBottom = 'none'

							return <MTableCell style={style} {...props} />
						}
					}}
				/>
			</div>
			<RefundDialog
				charges={refundGrouped}
				chargeAmount={chargeAmount}
				isShown={refundModal}
				refundCharge={processRefunds}
				loading={loading}
				setIsShown={() => setRefundModal(false)}
			/>
			<RetryChargeDialog
				isShown={retryModalOpen}
				retryCharge={retryCharge}
				charges={retryCharges}
				loading={loading}
				setIsShown={() => {
					setRetryCharges([])
					setRetryModalOpen(false)
				}}
			/>
		</MuiThemeProvider>
	)
}

export default TransactionsTable
